"use client";
import { type ComponentType, useState, useEffect, useRef } from "react";
import { getCameraTransform } from "@/engine/juice";
import { PixelText } from "@/components/ui/pixel-text";
import { BattleSprite as RawBattleSprite } from "./BattleSprite";
import { BattleOverlayHD, type Action } from "./BattleOverlayHD";
import { BattleMoveSelect } from "./BattleMoveSelect";
import { BattleBag, type ItemEntry } from "./BattleBag";
import { BattleParty, type PartyMember } from "./BattleParty";
import { BattleLog } from "./BattleLog";
import { BattleEffects, type EffectKind } from "./BattleEffects";
import { BattleScene } from "./BattleScene";
import { useGameStore } from "@/stores/game-store";
import type { StatusKind } from "@/stores/game-store";
import type { BattleParticipant } from "@/engine/battle/BattleEngine";

/**
 * BattleArena — self-contained battle flow controller.
 *
 * Owns the sub-screen state machine (main → moves | bag | party) and
 * orchestrates BattleOverlayHD, BattleMoveSelect, BattleBag, BattleParty,
 * BattleLog, and BattleEffects inside the 16:9 pixel-art panel.
 *
 * The parent wires handler props to the BattleEngine; this component only
 * routes input and renders the appropriate screen.
 */

// Sibling-agent BattleSprite contract (per PLAN-game-rebuild.md).
type SpriteOrientation = "front" | "back";
type SpriteState = "idle" | "attack" | "hurt" | "faint";
interface BattleSpriteProps {
  speciesId: string;
  orientation: SpriteOrientation;
  state?: SpriteState;
  scale?: number;
  level?: number;
  hp?: number;
  status?: StatusKind;
  battlePortrait?: boolean;
}
const BattleSprite = RawBattleSprite as unknown as ComponentType<BattleSpriteProps>;

export type MoveInfo = {
  id: string;
  name: string;
  type: string;
  power: number;
  accuracy: number;
  pp: number;
  ppMax: number;
};

export type ArenaEffectRequest = {
  id: string;
  kind: string;
  type?: string;
  origin: { x: number; y: number };
  target?: { x: number; y: number };
  onComplete?: () => void;
};

export type BattleArenaProps = {
  // Combatants
  player: BattleParticipant & { name?: string };
  opponent: BattleParticipant & { name?: string };
  // The full party for swap screen
  party: PartyMember[];
  // Available moves of the active Surggie
  moves: MoveInfo[];
  // Inventory for the bag screen
  items: ItemEntry[];
  // Active log lines
  log: string[];
  // Whether wild encounter (Run is allowed) or trainer (Run not allowed)
  isWild: boolean;
  // Effects to render
  effects: ArenaEffectRequest[];
  // Handlers — caller wires these to BattleEngine
  onMoveSelected: (moveId: string) => void;
  onItemUsed: (itemId: string) => void;
  onSurggieSwitch: (partyIndex: number) => void;
  onRun: () => void;
  // Whether the menu accepts input (false while resolving a turn)
  menuEnabled: boolean;
  // Biome backdrop key for BattleScene (Agent D passes from the active zone).
  backdropKey?: string;
};

type SubScreen = "main" | "moves" | "bag" | "party";

const FRAME_BORDER = "2px solid #1a1a1a";
const PAPER_CREAM = "#f5ecd6";
const PIXELATED = { imageRendering: "pixelated" as const };


function hpColor(ratio: number): string {
  // Earth tones only — no neon.
  if (ratio > 0.5) return "#5c8a3a"; // muted moss green
  if (ratio > 0.2) return "#b8923a"; // muted amber
  return "#a83a2a"; // muted brick red
}

// Per-status badge color + 3-letter glyph fallback, palette-conform
// (PIXEL_STYLE): poison=mystic-purple, burn=alert-red, paralyze=gold,
// sleep=ocean-blue. Used as a graceful fallback when the 16×16 PNG is absent.
const STATUS_BADGE: Record<
  Exclude<StatusKind, "none">,
  { bg: string; label: string }
> = {
  poison: { bg: "#a04ab8", label: "PSN" },
  burn: { bg: "#a83a2a", label: "BRN" },
  paralyze: { bg: "#c5a83a", label: "PAR" },
  sleep: { bg: "#3d6890", label: "SLP" },
};

// Status icon shown beside an InfoCard name. Prefers the 16×16 PNG; on a
// missing/broken file it swaps to a tiny palette-safe text badge so the
// affliction is never invisible.
function StatusIcon({ status }: { status: StatusKind }) {
  const [broken, setBroken] = useState(false);
  if (status === "none") return null;
  const badge = STATUS_BADGE[status];

  if (broken) {
    return (
      <span
        aria-label={badge.label}
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: 16,
          padding: "0 3px",
          background: badge.bg,
          color: PAPER_CREAM,
          border: "1px solid #1a1a1a",
          borderRadius: 2,
          fontFamily: "monospace",
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: "0.05em",
          lineHeight: 1,
          ...PIXELATED,
        }}
      >
        {badge.label}
      </span>
    );
  }

  return (
    <img
      src={`/assets/surggies/fx/status_${status}.png`}
      alt={badge.label}
      width={16}
      height={16}
      draggable={false}
      onError={() => setBroken(true)}
      style={{ display: "block", width: 16, height: 16, ...PIXELATED }}
    />
  );
}

interface InfoCardProps {
  name: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  align: "left" | "right";
  status?: StatusKind;
}

function InfoCard({ name, level, hpCurrent, hpMax, align, status = "none" }: InfoCardProps) {
  const ratio = hpMax > 0 ? Math.max(0, Math.min(1, hpCurrent / hpMax)) : 0;
  const fillWidth = `${ratio * 100}%`;
  const barColor = hpColor(ratio);

  return (
    <div
      className="inline-flex flex-col gap-1 px-3 py-2"
      style={{
        background: PAPER_CREAM,
        border: FRAME_BORDER,
        borderRadius: 2,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08)",
        textAlign: align,
        ...PIXELATED,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5">
          <PixelText
            as="span"
            glow="none"
            size="sm"
            className="!text-[#1a1a1a] !tracking-wider"
          >
            {name}
          </PixelText>
          <StatusIcon status={status} />
        </span>
        <PixelText
          as="span"
          glow="none"
          size="sm"
          className="!text-[#4a3a1a] !tracking-wider"
        >
          {`Lv ${level}`}
        </PixelText>
      </div>

      {/* HP bar: 200px wide × 8px tall, with dark frame */}
      <div
        style={{
          width: 200,
          height: 8,
          background: "#2a2418",
          border: FRAME_BORDER,
          position: "relative",
          ...PIXELATED,
        }}
        role="progressbar"
        aria-valuenow={hpCurrent}
        aria-valuemin={0}
        aria-valuemax={hpMax}
      >
        <div
          style={{
            width: fillWidth,
            height: "100%",
            background: barColor,
            transition: "width 200ms steps(8, end)",
          }}
        />
      </div>

      <PixelText
        as="span"
        glow="none"
        size="sm"
        className="!text-[#1a1a1a] !tracking-wider"
      >
        {`${hpCurrent} / ${hpMax}`}
      </PixelText>
    </div>
  );
}

// Cast string kind back to BattleEffects' EffectKind union at render time.
// Callers populate `kind` via the engine, which uses the same string set.
type RenderableEffect = Omit<ArenaEffectRequest, "kind"> & { kind: EffectKind };

export function BattleArena({
  player,
  opponent,
  party,
  moves,
  items,
  log,
  isWild,
  effects,
  onMoveSelected,
  onItemUsed,
  onSurggieSwitch,
  onRun,
  menuEnabled,
  backdropKey,
}: BattleArenaProps) {
  const [subScreen, setSubScreen] = useState<SubScreen>("main");
  const [confirmRun, setConfirmRun] = useState(false);

  // Camera juice (Track C): sample the decaying shake/zoom impulse bus each
  // frame and apply it to the 16:9 panel. getCameraTransform self-cleans expired
  // impulses and returns "none" when idle, so this is cheap to call every frame.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const loop = (now: number) => {
      const el = panelRef.current;
      if (el) el.style.transform = getCameraTransform(now);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Live HP from the store. Opponent already flows through BattleEffects; here
  // we read both sides so the InfoCards + faint animation react to live damage.
  // Fall back to the static `player` prop until the bridge seeds player HP, so
  // the very first frame still shows full HP rather than 0.
  const storeOpponentHp = useGameStore((s) => s.battle.opponentHpCurrent);
  const storePlayerHpCurrent = useGameStore((s) => s.battle.playerHpCurrent);
  const storePlayerHpMax = useGameStore((s) => s.battle.playerHpMax);

  // Status conditions from the shared store contract (ENGINE emits, bridge
  // mirrors). Drive the InfoCard icon + the full-sprite tint.
  const playerStatus = useGameStore((s) => s.battle.playerStatus.kind);
  const opponentStatus = useGameStore((s) => s.battle.opponentStatus.kind);

  const playerHpMax = storePlayerHpMax > 0 ? storePlayerHpMax : player.hpMax;
  const playerHpCurrent =
    storePlayerHpMax > 0 ? storePlayerHpCurrent : player.hpCurrent;

  const opponentFainted = storeOpponentHp <= 0;
  const playerFainted = playerHpMax > 0 && playerHpCurrent <= 0;

  const playerName = player.name ?? "Player";
  const opponentName = opponent.name ?? "Wild Surggie";

  const goMain = () => setSubScreen("main");

  // Escape key → back to main from any sub-screen.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && subScreen !== "main") {
        setSubScreen("main");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [subScreen]);

  const handleMainSelect = (action: Action) => {
    switch (action) {
      case "fight":
        setSubScreen("moves");
        break;
      case "bag":
        setSubScreen("bag");
        break;
      case "surggie":
        setSubScreen("party");
        break;
      case "run":
        if (isWild) {
          setConfirmRun(true);
        }
        // When not wild, Run button is grayed out — action won't fire.
        break;
    }
  };

  const handleMoveSelect = (moveId: string) => {
    onMoveSelected(moveId);
    setSubScreen("main");
  };

  const handleItemUse = (itemId: string) => {
    onItemUsed(itemId);
    setSubScreen("main");
  };

  const handleSwitch = (partyIndex: number) => {
    onSurggieSwitch(partyIndex);
    setSubScreen("main");
  };

  // Narrow each effect's kind to the BattleEffects EffectKind union.
  const renderableEffects: RenderableEffect[] = effects.map((e) => ({
    ...e,
    kind: e.kind as EffectKind,
  }));

  return (
    // Outer wrapper: fills viewport, centers the 16:9 panel.
    <div
      className="flex h-full w-full items-center justify-center"
      style={{ background: "#0a0a0a", ...PIXELATED }}
    >
      {/* 16:9 panel, scaled to fit using aspect-ratio. */}
      <div
        ref={panelRef}
        className="relative"
        style={{
          aspectRatio: "16 / 9",
          width: "min(100%, calc(100vh * 16 / 9))",
          maxHeight: "100vh",
          // Backmost biome scene is rendered by <BattleScene/> below; this
          // walnut fill only shows through the inset frame edges.
          background: "#5c4a30",
          border: FRAME_BORDER,
          boxShadow: "inset 0 0 0 4px #2a2418",
          overflow: "hidden",
          transformOrigin: "center center",
          willChange: "transform",
          ...PIXELATED,
        }}
      >
        {/* Backmost layer: biome-aware pixel-art scene (sky + scenery + ground
            + platforms + one-shot entry animation). Behind every other layer. */}
        <BattleScene backdropKey={backdropKey ?? "grass"} />

        {/* Safe area: padded inset frame so panels never touch the viewport
            edges. The log strip occupies the bottom 16%; the safe area sits
            above it (bottom inset = 16% + breathing room) and is padded on the
            other three sides. All scene children anchor to THIS box, not the
            raw panel edges, so fixed-width cards (the 200px HP bar) stay inside
            the frame at every responsive scale.

            The top inset is larger because the parent /game page overlays a
            fixed ~64px HUD bar (HudBar, position:absolute top-0) on top of this
            panel; the opponent card must clear it so its name/level row isn't
            hidden behind the HUD. */}
        <div
          className="absolute"
          style={{
            top: "clamp(72px, 11%, 96px)",
            left: "clamp(12px, 4%, 32px)",
            right: "clamp(12px, 4%, 32px)",
            bottom: "calc(16% + clamp(8px, 2%, 20px))",
            pointerEvents: "none",
          }}
        >
          {/* Opponent zone: top-right of the safe area. */}
          <div
            className="absolute"
            style={{
              top: 0,
              right: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 12,
              pointerEvents: "auto",
            }}
          >
            <InfoCard
              name={opponentName}
              level={opponent.level}
              hpCurrent={opponent.hpCurrent}
              hpMax={opponent.hpMax}
              align="right"
              status={opponentStatus}
            />
            <div style={PIXELATED}>
              <BattleSprite
                speciesId={opponent.speciesId}
                orientation="front"
                state={opponentFainted ? "faint" : "idle"}
                scale={3}
                level={opponent.level}
                hp={storeOpponentHp}
                status={opponentStatus}
                battlePortrait
              />
            </div>
          </div>

          {/* Player zone: bottom-left of the safe area. */}
          <div
            className="absolute"
            style={{
              bottom: 0,
              left: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              pointerEvents: "auto",
            }}
          >
            <div style={PIXELATED}>
              <BattleSprite
                speciesId={player.speciesId}
                orientation="back"
                state={playerFainted ? "faint" : "idle"}
                scale={3}
                level={player.level}
                hp={playerHpCurrent}
                status={playerStatus}
                battlePortrait
              />
            </div>
            <InfoCard
              name={playerName}
              level={player.level}
              hpCurrent={playerHpCurrent}
              hpMax={playerHpMax}
              align="left"
              status={playerStatus}
            />
          </div>
        </div>

        {/* Effects overlay: full panel, absolute, pointer-events: none.
            Mounted UNDER the menu so the menu remains interactive. */}
        <BattleEffects effects={renderableEffects} />

        {/* Menu slot: bottom-right, sitting just above the log strip and clear
            of the player zone. Anchored with padded insets so it never clips. */}
        <div
          className="absolute"
          style={{
            bottom: "calc(16% + clamp(8px, 2%, 20px))",
            right: "clamp(12px, 4%, 32px)",
            // Bag/Party screens are wider — widen on those, otherwise default.
            width:
              subScreen === "bag" || subScreen === "party" ? "52%" : "38%",
            maxWidth: "calc(100% - clamp(24px, 8%, 64px))",
            minHeight: "22%",
            // Sub-screen lock: dim and block pointer events while resolving.
            opacity: menuEnabled ? 1 : 0.5,
            pointerEvents: menuEnabled ? "auto" : "none",
            ...PIXELATED,
          }}
        >
          {subScreen === "main" && !confirmRun && (
            <BattleOverlayHD
              enabled={menuEnabled}
              canRun={isWild}
              onSelect={handleMainSelect}
            />
          )}

          {/* Run confirmation inline panel */}
          {subScreen === "main" && confirmRun && (
            <div
              style={{
                background: PAPER_CREAM,
                border: FRAME_BORDER,
                padding: "12px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                ...PIXELATED,
              }}
            >
              <PixelText as="div" glow="none" size="sm" className="!text-[#1a1a1a] !tracking-wider">
                Really run?
              </PixelText>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => { setConfirmRun(false); onRun(); }}
                  style={{
                    background: "#a83a2a",
                    color: PAPER_CREAM,
                    border: FRAME_BORDER,
                    padding: "4px 12px",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.75rem",
                    letterSpacing: "0.1em",
                    ...PIXELATED,
                  }}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRun(false)}
                  style={{
                    background: PAPER_CREAM,
                    color: "#1a1a1a",
                    border: FRAME_BORDER,
                    padding: "4px 12px",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.75rem",
                    letterSpacing: "0.1em",
                    ...PIXELATED,
                  }}
                >
                  NO
                </button>
              </div>
            </div>
          )}

          {subScreen === "moves" && (
            <BattleMoveSelect
              moves={moves}
              enabled={menuEnabled}
              onSelect={handleMoveSelect}
              onBack={goMain}
            />
          )}
          {subScreen === "bag" && (
            <BattleBag
              items={items}
              enabled={menuEnabled}
              onUse={handleItemUse}
              onBack={goMain}
            />
          )}
          {subScreen === "party" && (
            <BattleParty
              party={party}
              enabled={menuEnabled}
              onSwitch={handleSwitch}
              onBack={goMain}
            />
          )}
        </div>

        {/* Log strip: bottom */}
        <div
          className="absolute"
          style={{
            left: 0,
            right: 0,
            bottom: 0,
            height: "16%",
            background: PAPER_CREAM,
            borderTop: FRAME_BORDER,
            padding: "8px 16px",
            ...PIXELATED,
          }}
        >
          <BattleLog lines={log} visibleLines={2} />
        </div>
      </div>
    </div>
  );
}
